// CustomerOnboarding.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import backend from "../../../api";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, easeOut, AnimatePresence } from "framer-motion";
import Classes from "./Auth.module.css";
import { setUserData } from "../../../redux-store/auth/actions";

const LoadingSpinner = () => (
  <div className="loadingio-spinner-eclipse-nq4q5u6dq7r">
    <div className="ldio-x2uulkbinbj">
      <div></div>
    </div>
  </div>
);

const fadeInLeft = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -10 },
  transition: { duration: 0.5, ease: easeOut, delay: 0.3 },
};

function CustomerOnboarding() {
  const [state] = useState("createProfile");
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const location = useLocation();

  const { user } = useSelector((s) => s.auth);
  const userId = user?._id;

  const prefill = location?.state?.profilePrefill || {};

  const [profileName, setProfileName] = useState(prefill?.name || "");
  const [profilePhone, setProfilePhone] = useState(""); // local number
  const [profilePhoneCode, setProfilePhoneCode] = useState("+1"); // intl code

  const [studioId, setStudioId] = useState(""); // optional Studio (onboarding) id
  const [loading, setLoading] = useState(false);

  // helper — keep consistent with manufacturer flow
  const makeSlug = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 60);

  // Prefill phone from either profilePrefill or user (split into code + local)
  useEffect(() => {
    const existingPhone = prefill?.phone || user?.phone || "";
    let phone = existingPhone || "";
    let code = "+1";

    if (existingPhone && existingPhone.startsWith("+")) {
      const match = existingPhone.match(/^(\+\d{1,4})(.*)$/);
      if (match) {
        code = match[1] || "+1";
        phone = (match[2] || "").trim();
      }
    } else if (existingPhone) {
      // no + in stored phone, treat whole thing as local number
      phone = existingPhone;
    }

    setProfilePhone(phone);
    setProfilePhoneCode(code);
  }, [prefill, user]);

  // create a default onboarding doc for the customer (if Studio ID not provided)
  const createOnboarding = async ({ userId, companyName, phone }) => {
    const slug = makeSlug(companyName);
    const body = {
      userId, // associate user in onboarding (same as manufacturer)
      companyName, // treat as Studio/Company display name
      slug, // backend ensures uniqueness
      phone,
      websiteURL: "", // none for customer by default
    };
    const res = await backend.post(`/api/onboarding/`, body);
    if (res?.status === 200 && res?.data?.ok && res?.data?.data?._id) {
      return res.data.data._id;
    }
    return null;
  };

  // attach current user to an existing onboarding (Studio)
  const attachUserToOnboarding = async (id, userId) => {
    // keeps parity with manufacturer where userId is set on onboarding
    try {
      await backend.put(`/api/onboarding/${id}`, { userId });
    } catch (e) {
      // backend might already have userId; ignore non-fatal errors
      // console.warn("attachUserToOnboarding:", e?.response?.data || e.message);
    }
  };

  // update user profile with company, name, firstName, lastName, phone
  const updateUserWithCompany = async (companyId, name, phone) => {
    const trimmedName = (name || "").trim();
    let firstName = "";
    let lastName = "";

    if (trimmedName) {
      const parts = trimmedName.split(" ");
      firstName = parts[0] || "";
      lastName = parts.slice(1).join(" ").trim();
    }

    const body = {
      onboarded: true,
      companyId, // store onboarding _id as companyId in user profile
      name: trimmedName || "User",
      firstName,
      lastName,
      phone,
      role: "customer",
    };

    const response = await backend.put(`/api/user/${userId}`, body);
    if (response?.data?.data) {
      await dispatch(setUserData(response.data.data));
    }
  };

  const onFinishProfile = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert("Please sign in again to continue.");
      return;
    }
    setLoading(true);
    try {
      // Build full phone with intl code (same pattern as InfoTab)
      let code = profilePhoneCode || "";
      let local = profilePhone || "";

      if (code) {
        if (!code.startsWith("+")) {
          code = "+" + code.replace(/^\+*/, "");
        }
        code = code.replace(/[^\d+]/g, "");
      }

      local = local.replace(/\s|-/g, ""); // clean spaces/dashes

      const rawFullPhone = `${code}${local}`;
      const fullPhone = rawFullPhone.replace(/\s|-/g, "");

      let resolvedCompanyId = "";

      if (studioId?.trim()) {
        // verify Studio ID exists
        const check = await backend.get(`/api/onboarding/${studioId.trim()}`);
        const exists = check?.data?.data?._id;
        if (!exists) {
          alert("Invalid Studio ID. Please check and try again.");
          setLoading(false);
          return;
        }
        resolvedCompanyId = studioId.trim();
        await attachUserToOnboarding(resolvedCompanyId, userId);
      } else {
        // create default onboarding for this customer
        const displayName = profileName?.trim()
          ? `${profileName.trim()}'s Studio`
          : "New Studio";

        const createdId = await createOnboarding({
          userId,
          companyName: displayName,
          phone: fullPhone || "",
        });
        if (!createdId) {
          alert("Could not create a default Studio. Please try again.");
          setLoading(false);
          return;
        }
        resolvedCompanyId = createdId;
      }

      // update the user profile with companyId, name, firstName, lastName, phone
      await updateUserWithCompany(
        resolvedCompanyId,
        profileName?.trim() || "User",
        fullPhone || ""
      );

      // route
      if (location?.state?.from === "toTemplate") {
        navigateTo(`/templates`);
      } else {
        navigateTo("/products");
      }
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "An error occurred while finishing profile setup. Please try again.";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={Classes.signUp}>
        {/* LEFT SIDE */}
        <div className={Classes.signUpGridLeft}>
          <AnimatePresence mode="wait">
            {state === "createProfile" && (
              <motion.div key="create-profile" {...fadeInLeft} style={{ width: '100%' }} >
                <form
                  onSubmit={onFinishProfile}
                  className={Classes.signUpInnerCon}
                  style={{ margin: '0 auto' }}
                >
                  <a
                    href="/"
                    className={Classes.miniLogo2}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="logo">
                      <img src="/assets/images/logo.png" alt="logo" />
                    </div>
                  </a>

                  <h5 className={Classes.heading2}>Create Your Profile</h5>
                  <p
                    className={Classes.subtitle}
                    style={{ textAlign: "start" }}
                  >
                    Don’t worry – you can edit this later in your settings
                  </p>

                  {/* Name */}
                  <input
                    type="text"
                    required
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Enter your name"
                    className={`${Classes.inputField} ${Classes.mb20}`}
                    style={{ width: "100%" }}
                  />

                  {/* Phone: code + local number */}
                  <div
                    className={`${Classes.mb20}`}
                    style={{ display: "flex", gap: 12 }}
                  >
                    <input
                      type="text"
                      value={profilePhoneCode}
                      onChange={(e) => {
                        let v = e.target.value || "";

                        // Ensure a single "+" at the start if something is typed
                        if (v.length > 0 && !v.startsWith("+")) {
                          v = "+" + v.replace(/^\+*/, "");
                        }

                        // Only allow "+" and digits
                        v = v.replace(/[^\d+]/g, "");

                        setProfilePhoneCode(v);
                      }}
                      placeholder="+1"
                      className={Classes.inputField}
                      style={{ maxWidth: "110px" }}
                    />

                    <input
                      type="tel"
                      value={profilePhone}
                      onChange={(e) => {
                        let v = e.target.value || "";
                        // Allow digits, spaces and dashes in UI
                        v = v.replace(/[^\d\s-]/g, "");
                        setProfilePhone(v);
                      }}
                      placeholder="Enter your phone number"
                      className={Classes.inputField}
                      style={{ width: "100%" }}
                    />
                  </div>

                  {/* Studio ID (Onboarding ID) */}
                  <div className={Classes.mb20}>
                    <label
                      className={Classes.fieldLabel}
                      style={{ display: "block", marginBottom: 6 }}
                    >
                      Studio ID <span className={Classes.optionalTag}>Optional</span>
                    </label>
                    <input
                      type="text"
                      value={studioId}
                      onChange={(e) => setStudioId(e.target.value)}
                      placeholder="Paste existing Studio ID or leave blank to create one"
                      className={Classes.inputField}
                      style={{ width: "100%" }}
                    />
                  </div>

                  <button
                    type="submit"
                    className={`${Classes.button} ${Classes.signInButton}`}
                    disabled={loading}
                  >
                    Finish
                    {loading && <LoadingSpinner />}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT SIDE */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: easeOut }}
          className={Classes.signUpGridRight}
        >
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: easeOut, delay: 0.3 }}
            className={Classes.signUpInnerCon2}
          >
            <h5 className={Classes.title3}>
              Sign Up for Your 14 Day Free <br /> Trial Today
            </h5>
            <br />
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signin1.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Powerful Fixture Search</h5>
                <h5 className={Classes.miniText2}>
                  Instantly filter lighting products by specs like beam angle,
                  wattage, and color temp.
                </h5>
              </div>
            </div>
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signin2.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Export-Ready Output</h5>
                <h5 className={Classes.miniText2}>
                  Download spec sheets or Excel files formatted for
                  documentation and team sharing.
                </h5>
              </div>
            </div>
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signin3.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Organized Project Lists</h5>
                <h5 className={Classes.miniText2}>
                  Save selected fixtures to custom lists and manage them across
                  multiple projects on your account.
                </h5>
              </div>
            </div>
            <div className={Classes.infoElem}>
              <div className={Classes.infoImgBox}>
                <img
                  src="/assets/icons/signin4.png"
                  draggable={false}
                  alt=""
                />
              </div>
              <div className={Classes.infoElemRight}>
                <h5 className={Classes.title5}>Clean, Modern UI</h5>
                <h5 className={Classes.miniText2}>
                  Work faster with an intuitive, distraction-free interface that
                  puts lighting specs front and center—no clutter, just clarity.
                </h5>
              </div>
            </div>
          </motion.div>
          <img
            src="/assets/images/auth-bg.png"
            alt=""
            aria-hidden="true"
            className={Classes.authBg}
            draggable={false}
          />
        </motion.div>
      </div>
    </>
  );
}

export default CustomerOnboarding;
